import { useMutation } from '@apollo/client';
import {
  ApproveIdentityValidationCommand,
  IdentityValidationCommandActions,
  InitializeIdentityValidationT,
} from '@contexts/IdentityValidation/domain';
import { AptunoApolloLink } from '@shared/data-access';
import { ResponseApolloErrorMapping } from '@shared/data-mappers';
import { BaseMutationResponse2, Response } from '@shared/models';

import {
  ApproveIdentityValidationResponseDto,
  InitializeIdentityValidationRequestDto,
  InitializeIdentityValidationResponseDto,
} from '../dtos';
import {
  approveIdentityValidation,
  initializeIdentityValidation,
} from '../schemas';
import { useMemo } from 'react';

export const GqlIdentityValidationCommandActions: IdentityValidationCommandActions =
  {
    useInitializeIdentityValidation: (): InitializeIdentityValidationT => {
      const [action, { loading, data, error }] = useMutation<
        InitializeIdentityValidationResponseDto,
        InitializeIdentityValidationRequestDto
      >(initializeIdentityValidation, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const errorResult = useMemo(
        () => error && ResponseApolloErrorMapping(error),
        [error]
      );

      return {
        loading,
        error: errorResult,
        initializedIdentityValidation: data?.initializeIdentityValidation.value,
        initializeIdentityValidation: () => action(),
      };
    },

    useApproveIdentityValidation: (): BaseMutationResponse2<
      ApproveIdentityValidationCommand,
      boolean
    > => {
      const [action, { loading, data, error }] =
        useMutation<ApproveIdentityValidationResponseDto>(
          approveIdentityValidation,
          {
            errorPolicy: 'all',
            context: { clientName: AptunoApolloLink.CREDIT_STUDY },
          }
        );

      const result = useMemo(
        (): Response<boolean> =>
          !!error
            ? new Response.Failure(ResponseApolloErrorMapping(error))
            : !!data?.approveIdentityValidation?.value
            ? new Response.Success(true)
            : Response.notPerformed,
        [data, error]
      );

      return {
        result,
        executing: loading,
        execute: (command: ApproveIdentityValidationCommand) =>
          action({
            variables: {
              identityValidationId: command.identityValidationId.value,
              identityDocumentType: command.identityDocumentType,
              identityDocumentCode: command.identityDocumentCode.value,
              identityDocumentIssueDate:
                command.identityDocumentIssueDate?.value,
            },
          }),
      };
    },
  };
